import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TablePagination,
    Tooltip,
    Stack,
    Divider,
    TextField,
    InputAdornment,
    MenuItem,
    Skeleton,
    Button,
    Collapse,
    Grid,
    LinearProgress,
    Alert,
    Snackbar
} from '@mui/material';
import {
    InfoOutlined,
    History,
    AccountBalance,
    Search,
    FilterList,
    RestartAlt,
    CheckCircle,
    Cancel,
    Pending,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Payments
} from '@mui/icons-material';
import { useAllVendorPayouts } from 'hooks/usePayouts';
import { useDebounce } from 'hooks/useDebounce';
import { format } from 'date-fns';

const PayoutPage = () => {
    // --- PAGINATION STATE ---
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showFilters, setShowFilters] = useState(false);

    // --- UI State ---
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // --- FILTER STATES ---
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [maxAmount, setMaxAmount] = useState('');

    // --- DEBOUNCING ---
    // We debounce inputs that involve typing to prevent API spam
    const debouncedSearch = useDebounce(searchTerm, 500);
    const debouncedMin = useDebounce(minAmount, 500);
    const debouncedMax = useDebounce(maxAmount, 500);

    // --- DATA FETCHING (BACKEND FILTERING) ---
    const {
        data: responseData,
        isLoading,
        isFetching
    } = useAllVendorPayouts({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch,
        status: statusFilter === 'ALL' ? '' : statusFilter,
        startDate,
        endDate,
        minAmount: debouncedMin,
        maxAmount: debouncedMax
    });

    const payouts = responseData?.data || [];
    const totalCount = responseData?.total || 0;

    // Reset pagination to first page whenever filters change
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, statusFilter, startDate, endDate, debouncedMin, debouncedMax]);

    // --- HELPERS ---
    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('ALL');
        setStartDate('');
        setEndDate('');
        setMinAmount('');
        setMaxAmount('');
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return <CheckCircle fontSize="inherit" />;
            case 'pending':
                return <Pending fontSize="inherit" />;
            case 'rejected':
                return <Cancel fontSize="inherit" />;
            default:
                return null;
        }
    };

    return (
        <Box p={3} sx={{ bgcolor: '#fbfbfb', minHeight: '100vh' }}>
            {/* HEADER SECTION */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="flex-start" mb={4} spacing={2}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <History sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" fontWeight={800}>
                            Withdrawal Records
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Monitor and filter your payout history
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        startIcon={<FilterList />}
                        endIcon={showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        onClick={() => setShowFilters(!showFilters)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        {showFilters ? 'Hide Filters' : 'Advanced Filters'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Payments />}
                        onClick={() => setPayoutDialog({ open: true, amount: '' })}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        Request Payout
                    </Button>
                </Stack>
            </Stack>

            {/* SEARCH & FILTERS CARD */}
            <Card sx={{ p: 2, mb: 3, borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #eee' }}>
                <Stack spacing={2}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                placeholder="Search by reference, bank, account name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="action" />
                                        </InputAdornment>
                                    )
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                size="small"
                            >
                                <MenuItem value="ALL">All Statuses</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>

                    <Collapse in={showFilters}>
                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                        <Grid container spacing={2} pt={1}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="From Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="To Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Min Amount"
                                    value={minAmount}
                                    onChange={(e) => setMinAmount(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Max Amount"
                                    value={maxAmount}
                                    onChange={(e) => setMaxAmount(e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    fullWidth
                                    variant="text"
                                    color="error"
                                    startIcon={<RestartAlt />}
                                    onClick={handleClearFilters}
                                    sx={{ height: '40px', fontWeight: 700 }}
                                >
                                    Reset
                                </Button>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Stack>
            </Card>

            {/* MAIN TABLE CARD */}
            <Card
                sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: 'none', position: 'relative', overflow: 'hidden' }}
            >
                {isFetching && <LinearProgress sx={{ position: 'absolute', top: 0, width: '100%', height: 3 }} />}

                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f1f3f4' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Requested Date</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Account Details</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                Array(5)
                                    .fill(0)
                                    .map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={5}>
                                                <Skeleton height={50} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                            ) : payouts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                                        <Box opacity={0.5}>
                                            <Search sx={{ fontSize: 48, mb: 2 }} />
                                            <Typography variant="h6">No records found</Typography>
                                            <Typography variant="body2">Try adjusting your filters or search term</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                payouts.map((payout) => (
                                    <TableRow key={payout._id} hover sx={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={700}>
                                                {format(new Date(payout.createdAt), 'MMM dd, yyyy')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {format(new Date(payout.createdAt), 'p')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Box sx={{ bgcolor: '#f0f4ff', p: 1, borderRadius: 2, display: 'flex' }}>
                                                    <AccountBalance sx={{ fontSize: 18, color: 'primary.main' }} />
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {payout.accountName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {payout.bankName} • {payout.accountNumber}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight={900} color="secondary.main">
                                                ₦{payout.amount.toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Chip
                                                    icon={getStatusIcon(payout.status)}
                                                    label={payout.status.toUpperCase()}
                                                    color={getStatusColor(payout.status)}
                                                    size="small"
                                                    sx={{ fontWeight: 800, px: 0.5, fontSize: '0.65rem' }}
                                                />
                                                {payout.rejectionReason && (
                                                    <Tooltip title={`Admin Note: ${payout.rejectionReason}`}>
                                                        <InfoOutlined fontSize="small" color="error" sx={{ cursor: 'help' }} />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 0.5, borderRadius: 1 }}
                                            >
                                                #{payout._id.slice(-8).toUpperCase()}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Divider />

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Card>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default PayoutPage;
